import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

interface HeaderSlotContextValue {
  rightSlot: ReactNode;
  setRightSlot: (node: ReactNode) => void;
}

const HeaderSlotContext = createContext<HeaderSlotContextValue | undefined>(undefined);

export const useHeaderSlot = () => {
  const context = use(HeaderSlotContext);
  if (!context) {
    throw new Error('useHeaderSlot must be used within HeaderSlotProvider');
  }
  return context;
};

interface HeaderSlotProviderProps {
  children: ReactNode;
}

export const HeaderSlotProvider = ({ children }: HeaderSlotProviderProps) => {
  const [rightSlot, setRightSlotState] = useState<ReactNode>(null);

  const setRightSlot = useCallback((node: ReactNode) => {
    setRightSlotState(node);
  }, []);

  const value = useMemo(() => ({ rightSlot, setRightSlot }), [rightSlot, setRightSlot]);

  return <HeaderSlotContext value={value}>{children}</HeaderSlotContext>;
};

/**
 * Renders `node` into the shared Header's right slot for the lifetime of the calling
 * component (set on mount, cleared on unmount). Call it from a feature-module component
 * mounted under the router so the shared Header never imports the module (ADR-0090).
 *
 * Pass a STABLE node: the effect re-runs whenever `node` changes by reference, so an
 * inline element recreated every render (`useHeaderRightSlot(<VesselSwitcher />)` inside a
 * frequently re-rendering parent) re-sets the slot each commit. Memoize it
 * (`const slot = useMemo(() => <VesselSwitcher />, [])`) or render it from a component that
 * does not re-render on unrelated state.
 */
export const useHeaderRightSlot = (node: ReactNode) => {
  const { setRightSlot } = useHeaderSlot();

  useEffect(() => {
    setRightSlot(node);
    return () => {
      setRightSlot(null);
    };
  }, [node, setRightSlot]);
};
